import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../../../hooks/useI18n';
import { request, BaseResponse } from '../../../lib/api';
import { 
  getScenarioDetail, 
  ScenarioDetail,
} from '../../../lib/api/scenario';
import { Toast } from 'antd-mobile';
import logo3 from "@/assets/images/ai/logo3.png"
import coachImg from "@/assets/images/ai/coach.png"
import phImg from "@/assets/images/ai/ph.png"
import RolesDisplayCard from './components/RolesDisplayCard';
import RankLoader from '@/components/Loaders/RankLoader';
import { formatTextWithLineBreaks } from '../../../utils/textUtils';
// TypeScript 接口定义
interface UserAttemptInfo {
  attempt_times: number;
}

interface CompanyInfo {
  digital_human_end_time: string;
}

interface RuleItem {
  label: string;
  value: string;
}

// 扩展ScenarioDetail接口以包含用户相关信息
interface TrainingScenarioDetail extends ScenarioDetail {
  user_practice_count?: number; // 修改为从后端获取的真实练习次数
  passing_score?: number; // 添加成绩要求字段
}

// AI图标组件
const AIIcon: React.FC<{ openAnimation?: boolean; style?: React.CSSProperties }> = ({ 
  openAnimation = false, 
  style 
}) => {
  return (
    <div 
      className={`inline-block ${openAnimation ? 'animate-spin' : ''}`} 
      style={style}
    >
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
        <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
};

// 训练状态弹窗组件
const TrainStatusDialog: React.FC<{
  visible: boolean;
  message: string;
  btnText: string;
  onConfirm: () => void;
}> = ({ visible, message, btnText, onConfirm }) => {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-8"
          onClick={(e) => e.target === e.currentTarget && onConfirm()}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl"
          >
            <div className="text-center">
              <div className="text-sm font-medium text-gray-800 mb-6 leading-relaxed whitespace-pre-line">
                {message}
              </div>
              <button
                onClick={onConfirm}
                className="w-full py-3 bg-gradient-to-r from-purple-400 to-blue-400 text-white rounded-full font-medium hover:from-purple-500 hover:to-blue-500 transition-all duration-200 active:scale-95 text-sm"
              >
                {btnText}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};



// 发送消息组件（简化版）
const SendMessage: React.FC<{
  visible: boolean;
  onComplete: () => void;
  t: (key: string, fallback: string) => string;
}> = ({ visible, onComplete, t }) => {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1, y: "100%" }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed inset-0 bg-white z-50 flex flex-col"
        >
          <div className="flex-1 flex items-center justify-center px-8">
            <div className="text-center max-w-sm w-full">
              <div className="mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-base font-medium mb-4 text-gray-800">{t('ai.notifyAdmin', '通知管理员')}</h2>
                 <p className="text-gray-600 leading-relaxed text-sm">{t('ai.trainingLimitExceeded', '训练次数已达上限')}</p>
              </div>
              <button
                onClick={onComplete}
                className="w-full py-3 bg-gradient-to-r from-purple-400 to-blue-400 text-white rounded-full font-medium hover:from-purple-500 hover:to-blue-500 transition-all duration-200 active:scale-95 text-sm"
               >
                 {t('common.confirm', '确认')}
               </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const AiTrainingDetail: React.FC = () => {
  const { t } = useI18n();
  
  // 格式化时长为分秒
  const formatDuration = (seconds: number) => {
    if (seconds <= 0) return null;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return <>{minutes}{t('common.minute')}{remainingSeconds}{t('common.second')}</>;
    }
    return <>{remainingSeconds}{t('common.second')}</>;
  };

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const assignId = searchParams.get('assignId') || '';
  
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [scenarioData, setScenarioData] = useState<TrainingScenarioDetail | null>(null);
  const [showSendMessage, setShowSendMessage] = useState(false);
  const [showErrDialog, setShowErrDialog] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [btnText, setBtnText] = useState('');
  const [currentRoleId,setCurrentRoleId]=useState('');

  // 获取场景详情
  const fetchScenarioDetail = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getScenarioDetail(id);
      if (response.data) {
        setScenarioData(response.data);
        // 设置默认选中第一个角色
        if (response.data.ai_roles && response.data.ai_roles.length > 0) {
          setCurrentRoleId(response.data.ai_roles[0].scenario_role_id);
        }
      }
    } catch (error) {
      Toast.show({ content: t('ai.fetchScenarioFailed'), icon: 'fail' });
    } finally {
      setLoading(false);
    }
  };
  
  const trainRoles = scenarioData?.ai_roles?.[0];
  const humanInfo: CompanyInfo = {
    digital_human_end_time: '2024-12-31T10:00:00.000Z'
  };
  const userPracticeCount = scenarioData?.user_practice_count || 0;

  // 规则列表
  const ruleList: RuleItem[] = [
    {
      label: t('ai.attemptTimes'),
      value: 'max_attempt',
    },
    {
      label: t('ai.scoreRequirement'),
      value: 'pass_score',
    }
  ];

  // 生命周期
  useEffect(() => {
    fetchScenarioDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 工具函数

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return new Date(timeString).toLocaleString('zh-CN');
  };

  // 检查当前时间是否在练习时间范围内
  const checkPracticeTimeStatus = () => {
    const now = new Date();
    const startTime = scenarioData?.practice_time_start ? new Date(scenarioData.practice_time_start) : null;
    const endTime = scenarioData?.practice_time_end ? new Date(scenarioData.practice_time_end) : null;

    // 检查是否达到最大练习次数
    const maxAttempts = scenarioData?.max_practice_attempts || 0;
    if (maxAttempts > 0 && userPracticeCount >= maxAttempts) {
      return { 
        canPractice: false, 
        buttonText: t('ai.maxAttemptsReached', '达到最大次数'), 
        reason: `您已完成 ${userPracticeCount} 次练习，已达到最大练习次数限制 (${maxAttempts} 次)` 
      };
    }

    // 如果开始时间和结束时间都为null，表示不限时间
    if (!startTime && !endTime) {
      return { canPractice: true, buttonText: t('ai.startChat'), reason: '' };
    }

    // 如果只有开始时间，检查是否已开始
    if (startTime && !endTime) {
      if (now < startTime) {
        return { 
          canPractice: false, 
          buttonText: t('ai.notStarted', '未开始'), 
          reason: `练习将于 ${formatTime(scenarioData?.practice_time_start)} 开始` 
        };
      }
      return { canPractice: true, buttonText: t('ai.startChat'), reason: '' };
    }

    // 如果只有结束时间，检查是否已结束
    if (!startTime && endTime) {
      if (now > endTime) {
        return { 
          canPractice: false, 
          buttonText: t('ai.ended', '已结束'), 
          reason: `练习已于 ${formatTime(scenarioData?.practice_time_end)} 结束` 
        };
      }
      return { canPractice: true, buttonText: t('ai.startChat'), reason: '' };
    }

    // 如果开始时间和结束时间都存在
    if (startTime && endTime) {
      if (now < startTime) {
        return { 
          canPractice: false, 
          buttonText: t('ai.notStarted', '未开始'), 
          reason: `练习将于 ${formatTime(scenarioData?.practice_time_start)} 开始` 
        };
      }
      if (now > endTime) {
        return { 
          canPractice: false, 
          buttonText: t('ai.ended', '已结束'), 
          reason: `练习已于 ${formatTime(scenarioData?.practice_time_end)} 结束` 
        };
      }
      return { canPractice: true, buttonText: t('ai.startChat'), reason: '' };
    }

    return { canPractice: true, buttonText: t('ai.startChat'), reason: '' };
  };

  // API调用函数

  const getPermissionUseLog = async (): Promise<BaseResponse<{ use_limit: number; state: string }>> => {
    try {
      const response = await request<{ use_limit: number; state: string }>('/super/home/v1/market/PermissionUseLog/aiTrainPartner');
      return response;
    } catch (error) {
      console.error(t('ai.getPermissionLogFailed'), error);
      return { success: false };
    }
  };

  // 事件处理函数
  const handleStartChat = async () => {
    // 检查练习时间范围
    const timeStatus = checkPracticeTimeStatus();
    if (!timeStatus.canPractice) {
      setMessageText(timeStatus.reason);
      setBtnText(t('common.confirm', '确认'));
      setShowErrDialog(true);
      return;
    }

    try {
      setLoading(true);
      
      // 根据当前选中的角色ID找到对应的角色
      const selectedRole = scenarioData?.ai_roles?.find(role => role.scenario_role_id === currentRoleId);
      const roleId = selectedRole?.scenario_role_id || scenarioData?.ai_roles?.[0]?.scenario_role_id;
      
      // 调用创建session接口
      const sessionResponse = await request<{
        session_id: string;
        session_token: string;
        status: string;
      }>('/conversations/scenario-sessions', {
        method: 'POST',
        data: {
          scenario_id: id,
          language_code: scenarioData?.scene_lang || 'zh-CN', // 使用场景语言
          ai_role_id: roleId
        }
      });
      
      if (sessionResponse.success && sessionResponse.data) {
        // 使用session_id跳转到对话页面，将scene_lang添加到URL参数中以避免刷新后丢失
        const sceneLang = scenarioData?.scene_lang || 'zh-CN';
        navigate(`/ai-training/chat/${sessionResponse.data.session_id}?scene_lang=${sceneLang}`, {
          state: { 
            scenarioData,
            sessionId: sessionResponse.data.session_id,
            sessionToken: sessionResponse.data.session_token,
            roleData: selectedRole
          }
        });
      } else {
        Toast.show({ content: t('ai.createSessionFailed'), icon: 'fail' });
      }
    } catch (error) {
      console.error(t('ai.createSessionFailed'), error);
      Toast.show({ content: t('ai.createSessionFailedRetry'), icon: 'fail' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLog = () => {
    navigate(`/ai-training/history/${id}`);
  };

  const handleGoToRank = () => {
    navigate(`/ai-training/rank/list/${id}`);
  };

  const handleCloseErrDialog = () => {
    setShowErrDialog(false);
  };

  const handleCloseSendMessage = () => {
    setShowSendMessage(false);
  };

  // 加载状态检查
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <RankLoader />
      </div>
    );
  }

  if (!scenarioData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="text-gray-500">{t('ai.scenarioNotFound')}</div>
      </div>
    );
  }

  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-b from-purple-50 to-white relative" 
      style={{paddingLeft: '0px', paddingRight: 'px', paddingTop: '0px', paddingBottom: '120px'}}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {/* 响应式容器 */}
      <div className="max-w-sm mx-auto md:max-w-2xl lg:max-w-2xl xl:max-w-3xl px-0 md:px-4 lg:px-6">
        {/* 主要内容区域 */}
        <div className="relative">
        {/* 角色展示和选择卡片 */}
        <motion.div 
          style={{}}
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <RolesDisplayCard
            roles={scenarioData?.ai_roles || []}
            currentRoleId={currentRoleId}
            onRoleChange={(roleId) => {
              console.log('角色切换:', roleId);
              setCurrentRoleId(roleId)
              // 这里可以添加角色切换的逻辑
            }}
            sceneLang={scenarioData?.scene_lang || 'zh-CN'}
          />
        </motion.div>

        {/* 场景名称 */}
        <motion.div 
          className="bg-white  md:mx-0 bg-opacity-50 rounded-2xl shadow-lg" 
          style={{marginTop: '12px', padding: '32px', paddingBottom: '24px'}}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
        >
          <div className="flex items-center">
            <img
              className=""
              src={logo3}
              alt=""
              style={{width: '24px', height: '24px', marginRight: '8px'}}
            />
            <span className="text-gray-800 text-base font-medium">
               {t('ai.sceneName')}
             </span>
          </div>
          <div style={{marginTop: '12px'}}>
            <span className="text-gray-600 text-sm leading-relaxed">
              {scenarioData.name}
            </span>
          </div>
        </motion.div>

        {/* 场景描述 */}
        <motion.div 
          className="bg-white md:mx-0 bg-opacity-50 rounded-2xl shadow-lg" 
          style={{marginTop: '12px', padding: '32px', paddingBottom: '24px'}}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
        >
          <div className="flex items-center">
            <img
              className=""
              src={logo3}
              alt=""
              style={{width: '24px', height: '24px', marginRight: '8px'}}
            />
            <span className="text-gray-800 text-base font-medium">
               {t('ai.sceneDescription')}
             </span>
          </div>
          <div className="text-gray-600 text-sm leading-relaxed" style={{marginTop: '12px'}}>
            <div dangerouslySetInnerHTML={{ __html: formatTextWithLineBreaks(scenarioData.description) }} />
          </div>
        </motion.div>

        {/* 考核标准 */}
        <motion.div 
          className="bg-white md:mx-0 bg-opacity-50 rounded-2xl shadow-lg" 
          style={{marginTop: '12px', padding: '32px', paddingBottom: '24px'}}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
        >
          <div className="flex items-center">
            <img
              className=""
              src={logo3}
              alt=""
              style={{width: '24px', height: '24px', marginRight: '8px'}}
            />
            <span className="text-gray-800 text-base font-medium">
               {t('ai.assessmentCriteria')}
             </span>
          </div>
          
          {/* 考核关键点 */}
          {scenarioData.key_points && (
            <>
              <div className="flex mb-4" style={{marginTop: '12px'}}>
                <div className="bg-purple-100 rounded-lg text-gray-800 text-xs" style={{paddingTop: '2px', paddingBottom: '2px', paddingLeft: '6px', paddingRight: '6px'}}>
                   {t('ai.keyPoints')}
                 </div>
              </div>
              <div className="text-gray-600 text-sm leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: formatTextWithLineBreaks(scenarioData.key_points) }} />
              </div>
            </>
          )}
          
          {/* 陪练规则 */}
          <div className="flex" style={{marginTop: '12px'}}>
            <div className="bg-purple-100 rounded-lg text-gray-800 text-xs" style={{paddingTop: '2px', paddingBottom: '2px', paddingLeft: '6px', paddingRight: '6px'}}>
               {t('ai.trainingRules')}
             </div>
          </div>

          <div className="flex flex-wrap" style={{marginTop: '8px'}}>
            <div className="border-2 border-gray-300 rounded text-gray-600 text-sm" style={{paddingLeft: '16px', paddingRight: '16px', paddingTop: '4px', paddingBottom: '4px', marginRight: '12px', marginBottom: '8px'}}>
               {t('ai.minDuration')}：
               {(scenarioData.min_practice_duration || 0) > 0 ? (
                 formatDuration(scenarioData.min_practice_duration || 0)
               ) : (
                 <>{t('ai.noTimeLimit')}</>
               )}
             </div>
            
            <div className="border-2 border-gray-300 rounded text-gray-600 text-sm" style={{paddingLeft: '16px', paddingRight: '16px', paddingTop: '4px', paddingBottom: '4px', marginRight: '12px', marginBottom: '8px'}}>
               {t('ai.maxDuration')}：
               {(scenarioData.max_practice_duration || 0) > 0 ? (
                 formatDuration(scenarioData.max_practice_duration || 0)
               ) : (
                 <>{t('ai.noTimeLimit')}</>
               )}
             </div>
            
            {ruleList.map((item, index) => (
              <motion.div
                key={index}
                className="border-2 border-gray-300 rounded text-gray-600 text-sm"
                style={{paddingLeft: '16px', paddingRight: '16px', paddingTop: '4px', paddingBottom: '4px', marginRight: '12px', marginBottom: '8px'}}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
              >
                {item.label}：
                 {item.value === 'pass_score' ? (
                   <>≥{scenarioData.passing_score || 80}{t('common.score')}</>
                 ) : item.value === 'max_attempt' ? (
                   <>{userPracticeCount}/{scenarioData.max_practice_attempts || 3}</>
                 ) : (
                   <>≤{item.value}</>
                 )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* 底部操作栏 */}
      <motion.div 
        className="fixed bottom-0 left-0 right-0 bg-white shadow-lg flex items-center justify-between border-t border-gray-100" 
        style={{paddingTop: '12px', paddingBottom: '32px', paddingLeft: '16px', paddingRight: '16px'}}
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <motion.div 
           className="bg-white bg-opacity-60 rounded-full shadow-lg cursor-pointer hover:bg-opacity-80 transition-all duration-200 flex-shrink-0"
           onClick={handleGoToRank}
           whileHover={{ scale: 1.1, rotate: 5 }}
           whileTap={{ scale: 0.95 }}
           transition={{ type: "spring", stiffness: 400, damping: 17 }}
         >
           <img
             className=""
             src={phImg}
             alt="排行榜"
             style={{width: '50px', height: '45px'}}
           />
         </motion.div>
        
        {(() => {
          const timeStatus = checkPracticeTimeStatus();
          return scenarioData ? (
            <div className="flex items-center space-x-4">
               <motion.button
                  className="bg-white py-2 px-6 bg-opacity-60 border-2 border-purple-500 text-purple-500 rounded-full shadow-lg font-medium hover:bg-purple-50 hover:border-purple-600 transition-all duration-200 active:scale-95 text-sm"
                  onClick={handleGoToLog}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  {t('ai.practiceRecord')}
                </motion.button>
                <motion.button
                  className={`${timeStatus.canPractice 
                    ? 'bg-gradient-to-r from-purple-400 to-blue-400 hover:from-purple-500 hover:to-blue-500' 
                    : 'bg-gray-400 cursor-not-allowed'
                  } text-white  py-2 px-6 rounded-full shadow-lg font-medium transition-all duration-200 active:scale-95 text-sm`}
                  onClick={handleStartChat}
                  disabled={!timeStatus.canPractice}
                  whileHover={timeStatus.canPractice ? { scale: 1.05, y: -2 } : {}}
                  whileTap={timeStatus.canPractice ? { scale: 0.98 } : {}}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  {timeStatus.buttonText}
                </motion.button>
             </div>
          ) : (
            <motion.button
                className={`${timeStatus.canPractice 
                  ? 'bg-gradient-to-r from-purple-400 to-blue-400 hover:from-purple-500 hover:to-blue-500' 
                  : 'bg-gray-400 cursor-not-allowed'
                } text-white rounded-full shadow-lg font-medium flex-1 max-w-xs transition-all duration-200 active:scale-95 text-sm`}
                onClick={handleStartChat}
                disabled={!timeStatus.canPractice}
                style={{paddingTop: '12px', paddingBottom: '12px', width: '80%'}}
                whileHover={timeStatus.canPractice ? { scale: 1.05, y: -2 } : {}}
                whileTap={timeStatus.canPractice ? { scale: 0.98 } : {}}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                {timeStatus.buttonText}
              </motion.button>
          );
        })()}
      </motion.div>

      {/* 弹窗组件 */}
       <SendMessage 
         visible={showSendMessage} 
         onComplete={handleCloseSendMessage}
         t={t}
       />
      
      <TrainStatusDialog
        visible={showErrDialog}
        message={messageText}
        btnText={btnText}
        onConfirm={handleCloseErrDialog}
      />
      </div>
    </motion.div>
  );
};

export default AiTrainingDetail;